import { getScrollParents, useLayoutEffect } from '@react-aria/utils'
import type { RefObject } from 'react'

type LockState = {
  count: number
  overflowX: string
  overflowY: string
  scrollbarGutter: string
}

// Reference counts per element so overlapping overlays sharing a scroll
// ancestor only restore the original styles when the last one unlocks.
const locks = new WeakMap<HTMLElement, LockState>()

function lock(node: HTMLElement) {
  const existing = locks.get(node)
  if (existing) {
    existing.count++
    return
  }

  locks.set(node, {
    count: 1,
    // Longhands, since setting `overflow` overwrites inline overflow-x/y
    overflowX: node.style.overflowX,
    overflowY: node.style.overflowY,
    scrollbarGutter: node.style.scrollbarGutter,
  })

  const widthBefore = node.clientWidth
  node.style.overflow = 'hidden'
  // Removing a classic scrollbar widens the content box. Keep the gutter
  // so content does not shift while locked.
  if (node.clientWidth > widthBefore) {
    node.style.scrollbarGutter = 'stable'
  }
}

function unlock(node: HTMLElement) {
  const state = locks.get(node)
  if (!state) return

  state.count--
  if (state.count > 0) return

  node.style.removeProperty('overflow')
  node.style.overflowX = state.overflowX
  node.style.overflowY = state.overflowY
  node.style.scrollbarGutter = state.scrollbarGutter
  locks.delete(node)
}

/**
 * Locks every scrollable ancestor of `ref` while `isLocked`.
 *
 * `usePreventScroll` only locks the document, so a trigger inside its own
 * scroll container (e.g. `Panel.Body`) would still scroll (UXI-2928).
 * Overlays render in a portal, so scrolling inside the overlay is unaffected.
 */
export function useLockScrollParents(
  ref: RefObject<Element | null> | undefined,
  isLocked: boolean
) {
  useLayoutEffect(() => {
    const element = ref?.current
    if (!isLocked || !element?.parentElement) return

    // Stops before <html>, which `usePreventScroll` already handles
    const nodes = getScrollParents(element.parentElement) as HTMLElement[]
    nodes.forEach(lock)

    return () => nodes.forEach(unlock)
  }, [ref, isLocked])
}
